/**
 * W1-11b (app-shell-ui): the toast stack (bottom left, above the hint bar; DESIGN.md §4.4, U§5 A6).
 *
 * W3-01 (UX-013): the stack sits at the bottom left, over the tool rail and the time gutter rather than the inspector
 * fields, and above whatever bar is at the bottom of the page (the editor's hint bar, the Library's storage footer;
 * measured), so it never covers working controls such as the footer's Trash link. Action buttons never shrink or
 * truncate ("Restore", not "Rest..."); the message wraps instead.
 *
 * Each toast pairs an icon and a word with its colour (never colour alone), counts repeats ("x3"), and offers its
 * Undo link or command action. The Undo link runs Edit > Undo only while the step the toast describes is still the
 * last applied one (recorded when the toast was raised); after another edit, an undo or a project switch the link
 * goes away, so it never undoes a different step. Toasts close themselves after their duration; the timer pauses
 * while the pointer is over the toast or focus is inside it (and restarts afterwards). Errors are announced
 * assertively (role="alert"), the rest politely (role="status"). Toast buttons do not take focus on pointer down.
 */

import { useCallback, useEffect, useLayoutEffect, useState, useSyncExternalStore } from 'react';
import { Button } from '../../ui/Button';
import { Icon } from '../../ui/Icon';
import { IconButton } from '../../ui/IconButton';
import type { Unsubscribe } from '../../core/util/types';
import type { AppServices } from '../registry/types';
import {
  undoTargetCurrent,
  type ShellUiService,
  type ToastEntry,
  type ToastKind,
  type ToastUndoTarget,
  type UiSnapshot,
} from '../uiService';
import { useStore, useText, useUi } from './ShellContext';
import { m } from './shell.messages';
import s from './Shell.module.css';

const ICONS: Readonly<Record<ToastKind, string>> = {
  info: 'Info',
  success: 'CircleCheck',
  warning: 'TriangleAlert',
  error: 'CircleX',
};

const selectToasts = (snap: UiSnapshot): readonly ToastEntry[] => snap.toasts;

/** Bars at the bottom of the page the toasts stay above: the editor's hint bar, the Library's storage footer. */
const BOTTOM_BARS = 'footer[data-hint-mode], [data-region="library"] > footer';
/** Gap between the bottom bar and the stack (CSS px). */
const STACK_GAP = 16;

/** Distance (CSS px) from the window's bottom edge to the top of the lowest bottom bar on screen, or null. */
export function bottomBarOffset(doc: Document, viewportHeight: number): number | null {
  let top: number | null = null;
  for (const el of doc.querySelectorAll<HTMLElement>(BOTTOM_BARS)) {
    const r = el.getBoundingClientRect();
    if (r.height <= 0 || r.bottom < viewportHeight - 2) continue;
    top = top === null ? r.top : Math.min(top, r.top);
  }
  return top === null ? null : Math.max(0, viewportHeight - top);
}

/** Bottom offset of the stack, measured whenever toasts change and when the window resizes. */
function useStackBottom(count: number): number | null {
  const [bottom, setBottom] = useState<number | null>(null);
  useLayoutEffect(() => {
    if (count === 0 || typeof document === 'undefined') return undefined;
    const measure = (): void => {
      const offset = bottomBarOffset(document, window.innerHeight);
      const next = offset === null ? null : Math.round(offset + STACK_GAP);
      setBottom((prev) => (prev === next ? prev : next));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('resize', measure);
    };
  }, [count]);
  return bottom;
}

export function Toasts(props: { readonly services: AppServices; readonly ui: ShellUiService }) {
  const toasts = useUi(props.ui, selectToasts);
  const text = useText(props.services);
  const bottom = useStackBottom(toasts.length);
  if (toasts.length === 0) return null;
  return (
    <section
      className={s.toasts}
      aria-label={text.t(m.toastsLabel)}
      data-chrome=""
      data-toasts=""
      style={bottom === null ? undefined : { bottom }}
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} services={props.services} ui={props.ui} />
      ))}
    </section>
  );
}

/** The toast's undo step is still the one Edit > Undo would undo (follows the open project's history). */
function useUndoCurrent(services: AppServices, target: ToastUndoTarget | null): boolean {
  const store = useStore(services);
  const subscribe = useCallback(
    (cb: () => void): Unsubscribe => store?.history.subscribe(cb) ?? (() => undefined),
    [store],
  );
  const get = useCallback((): boolean => undoTargetCurrent(target, store), [target, store]);
  return useSyncExternalStore(subscribe, get, get);
}

function ToastItem(props: { readonly toast: ToastEntry; readonly services: AppServices; readonly ui: ShellUiService }) {
  const { toast, services, ui } = props;
  const text = useText(services);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const paused = hovered || focused;
  const undoCurrent = useUndoCurrent(services, toast.undoTarget);

  useEffect(() => {
    if (paused) return undefined;
    const timer = setTimeout(() => {
      ui.dismissToast(toast.id);
    }, toast.durationMs);
    return () => {
      clearTimeout(timer);
    };
  }, [paused, toast.id, toast.revision, toast.durationMs, ui]);

  const dismiss = (): void => {
    ui.dismissToast(toast.id);
  };
  const action = toast.action;

  return (
    <div
      className={s.toast}
      data-kind={toast.kind}
      role={toast.kind === 'error' ? 'alert' : 'status'}
      aria-live={toast.kind === 'error' ? 'assertive' : 'polite'}
      onPointerEnter={() => {
        setHovered(true);
      }}
      onPointerLeave={() => {
        setHovered(false);
      }}
      onFocus={() => {
        setFocused(true);
      }}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setFocused(false);
      }}
    >
      <span className={s.toastIcon} aria-hidden="true">
        <Icon name={ICONS[toast.kind]} size={16} />
      </span>
      <span className={s.toastKind}>{text.t(m.toastKind, { kind: toast.kind })}</span>
      <span className={s.toastMessage}>{text.tr(toast.message)}</span>
      {toast.count > 1 && <span className={s.toastCount}>{text.t(m.toastCount, { count: toast.count })}</span>}
      {toast.undo && undoCurrent && (
        <Button
          variant="ghost"
          size="sm"
          icon="Undo2"
          onClick={() => {
            dismiss();
            if (undoTargetCurrent(toast.undoTarget, services.store)) {
              void services.registry.run('edit.undo', undefined, 'button');
            }
          }}
        >
          {text.t(m.toastUndo)}
        </Button>
      )}
      {action !== null && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            dismiss();
            void services.registry.runAny(action.command, action.args, 'button');
          }}
        >
          {text.tr(action.label)}
        </Button>
      )}
      <IconButton icon="X" size="sm" label={{ key: m.toastDismiss }} onClick={dismiss} />
    </div>
  );
}
